import React, { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom'; 
import { 
  RefreshCw, X, Search, Calendar, CheckCircle2, 
  Filter, UserCircle2, Layers, Fingerprint,
  FileCheck2, AlertCircle, Zap, ChevronRight, ChevronLeft, Trash2,
  List, Eye, FileJson, LayoutTemplate, Loader2
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const API_BASE = process.env.REACT_APP_API_URL || "http://127.0.0.1:8000/api";

const getErrorCodeName = (code) => {
  if (code.includes('XML1')) return "Lỗi Hành chính (XML1)";
  if (code.includes('XML2')) return "Lỗi Danh mục Thuốc (XML2)";
  if (code.includes('DVKT')) return "Lỗi Dịch vụ Kỹ thuật (XML3)";
  if (code.includes('VTYT')) return "Lỗi Vật tư Y tế (XML3)";
  if (code.includes('NOT_FOUND')) return "Lỗi Không tìm thấy Danh mục";
  if (code.includes('INVALID')) return "Lỗi Sai lệch thông tin";
  return "Lỗi Hệ thống";
};

const getLoaiKCBName = (code) => {
  if (!code) return '---';
  const strCode = String(code).trim();
  const map = {
    '1': 'Khám bệnh', '01': 'Khám bệnh',
    '2': 'Ngoại trú', '02': 'Điều trị ngoại trú',
    '3': 'Nội trú', '03': 'Điều trị nội trú',
    '4': 'Ngoại trú', '04': 'Điều trị nội trú ban ngày',
    '5': 'Nội trú', '09': 'Điều trị nội trú < 4h'
  };
  return map[strCode] || strCode;
};

const formatDateTime = (val) => {
  if (!val) return '---';
  const s = String(val).trim();
  if (s.includes('/')) return s;
  if (s.length >= 12 && !s.includes('-')) {
    return `${s.slice(6,8)}/${s.slice(4,6)}/${s.slice(0,4)} ${s.slice(8,10)}:${s.slice(10,12)}`;
  }
  if (s.length === 8 && !s.includes('-')) {
    return `${s.slice(6,8)}/${s.slice(4,6)}/${s.slice(0,4)}`;
  }
  const d = new Date(val);
  if (!isNaN(d.getTime())) {
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    const hours = String(d.getHours()).padStart(2, '0');
    const minutes = String(d.getMinutes()).padStart(2, '0');
    return `${day}/${month}/${year} ${hours}:${minutes}`;
  }
  return s;
};

const formatDateVN = (dateString) => {
  if (!dateString) return '';
  const [year, month, day] = dateString.split('-');
  return `${day}/${month}/${year}`;
};

const XmlErrorDataGrid = () => {
  const [allSubmissionsRaw, setAllSubmissionsRaw] = useState([]);
  const [viewMode, setViewMode] = useState('ERRORS'); // 'ERRORS' | 'ALL'
  
  const [loading, setLoading] = useState(false);
  const [isRechecking, setIsRechecking] = useState(false);
  const [isRecheckingErrors, setIsRecheckingErrors] = useState(false);
  
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  
  const [selectedErrorCode, setSelectedErrorCode] = useState('ALL'); 
  const [inspectingRecord, setInspectingRecord] = useState(null); 
  const [isSidebarClosing, setIsSidebarClosing] = useState(false);
  
  const [xmlDetailsModal, setXmlDetailsModal] = useState({ visible: false, data: null, loading: false, activeTab: 'XML1' });
  
  const [toastMessage, setToastMessage] = useState(null);
  
  const today = new Date().toISOString().split('T')[0];
  const [fromDate, setFromDate] = useState(today);
  const [toDate, setToDate] = useState(today);

  // Thêm State Tìm kiếm & Phân trang
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);

  const navigate = useNavigate();

  const fetchData = async () => {
    const token = localStorage.getItem("admin_token");
    if (!token) { navigate('/login'); return; }
    
    setLoading(true);
    try {
      const res = await axios.get(`${API_BASE}/submissions`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { from_ngay_ra: fromDate, to_ngay_ra: toDate }
      });
      setAllSubmissionsRaw(res.data);
      setSelectedErrorCode('ALL');
    } catch (err) {
      if (err.response?.status === 401) navigate('/login');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const activeSubmissions = useMemo(() => {
    if (viewMode === 'ALL') return allSubmissionsRaw;
    return allSubmissionsRaw.filter(sub => sub.status !== 'Hop_le' && sub.errors?.length > 0);
  }, [allSubmissionsRaw, viewMode]);

  const groupedErrors = useMemo(() => {
    const groups = {};
    activeSubmissions.forEach(sub => {
      sub.errors.forEach(err => {
        const code = err.error_code || 'UNKNOWN_ERROR';
        if (!groups[code]) {
          groups[code] = { code, name: getErrorCodeName(code), records: new Set() };
        }
        groups[code].records.add(sub.id);
      });
    });
    return Object.values(groups).sort((a, b) => b.records.size - a.records.size);
  }, [activeSubmissions]);

  // Bộ lọc dữ liệu hiển thị (kết hợp Tìm kiếm và Loại lỗi)
  const filteredAndSearchedRecords = useMemo(() => {
    let records = activeSubmissions;

    if (selectedErrorCode !== 'ALL') {
      const activeGroup = groupedErrors.find(g => g.code === selectedErrorCode);
      if (activeGroup) {
        records = records.filter(sub => activeGroup.records.has(sub.id));
      } else {
        records = [];
      }
    }

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      records = records.filter(sub => 
        (sub.ma_lk && String(sub.ma_lk).toLowerCase().includes(term)) ||
        (sub.ma_bn && String(sub.ma_bn).toLowerCase().includes(term)) ||
        (sub.ho_ten && String(sub.ho_ten).toLowerCase().includes(term))
      );
    }

    return records;
  }, [activeSubmissions, selectedErrorCode, groupedErrors, searchTerm]);

  // Reset trang về 1 khi đổi bộ lọc
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, selectedErrorCode, viewMode, pageSize]);

  // Dữ liệu đã phân trang
  const totalPages = Math.ceil(filteredAndSearchedRecords.length / pageSize) || 1;
  const paginatedRecords = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredAndSearchedRecords.slice(start, start + pageSize);
  }, [filteredAndSearchedRecords, currentPage, pageSize]);

  const handleRecheck = async () => {
    const token = localStorage.getItem("admin_token");
    if (!token) { navigate('/login'); return; }

    setIsRechecking(true);
    try {
      const res = await axios.post(`${API_BASE}/submissions/recheck`, null, {
        headers: { Authorization: `Bearer ${token}` },
        params: { from_ngay_ra: fromDate, to_ngay_ra: toDate }
      });
      showToast(`Đã chạy lại giám định ${res.data.count} hồ sơ thành công!`);
      fetchData(); 
    } catch (err) {
      showToast('Có lỗi xảy ra khi giám định lại!', true);
    } finally {
      setIsRechecking(false);
    }
  };

  const handleRecheckErrorsOnly = async () => {
    const errorIds = allSubmissionsRaw.filter(s => s.status !== 'Hop_le').map(s => s.id);
    if (errorIds.length === 0) {
      showToast('Không có hồ sơ lỗi nào để chạy lại!', true);
      return;
    }

    const token = localStorage.getItem("admin_token");
    if (!token) { navigate('/login'); return; }

    setIsRecheckingErrors(true);
    try {
      const res = await axios.post(`${API_BASE}/submissions/recheck`, 
        { submission_ids: errorIds }, 
        {
          headers: { Authorization: `Bearer ${token}` },
          params: { from_ngay_ra: fromDate, to_ngay_ra: toDate, only_errors: true }
        }
      );
      showToast(`Đã giám định lại ${res.data.count || errorIds.length} hồ sơ lỗi!`);
      fetchData(); 
    } catch (err) {
      showToast('Có lỗi xảy ra khi giám định lại hồ sơ lỗi!', true);
    } finally {
      setIsRecheckingErrors(false);
    }
  };

  const handleDeleteData = async () => {
    const token = localStorage.getItem("admin_token");
    if (!token) { navigate('/login'); return; }

    setIsDeleting(true);
    try {
      const res = await axios.delete(`${API_BASE}/submissions`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { from_ngay_ra: fromDate, to_ngay_ra: toDate }
      });
      
      if (res.data.deleted_count === 0) {
        showToast(res.data.message, true);
      } else {
        showToast(res.data.message);
      }
      
      setShowDeleteModal(false);
      fetchData(); 
    } catch (err) {
      showToast(err.response?.data?.detail || 'Có lỗi xảy ra khi xóa dữ liệu!', true);
    } finally {
      setIsDeleting(false);
    }
  };

  const openXmlDetails = async (subId) => {
    const token = localStorage.getItem("admin_token");
    if (!token) return;

    setXmlDetailsModal({ visible: true, data: null, loading: true, activeTab: 'XML1' });
    try {
      const res = await axios.get(`${API_BASE}/submissions/${subId}/details`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const availableTabs = Object.keys(res.data).filter(k => k.startsWith('XML') && res.data[k]);
      setXmlDetailsModal({ 
        visible: true, 
        data: res.data, 
        loading: false, 
        activeTab: availableTabs.includes('XML1') ? 'XML1' : availableTabs[0] || 'LỖI'
      });
    } catch (err) {
      showToast('Không thể tải chi tiết XML', true);
      setXmlDetailsModal({ visible: false, data: null, loading: false, activeTab: 'XML1' });
    }
  };

  const closeSidebar = () => {
    setIsSidebarClosing(true);
    setTimeout(() => {
      setInspectingRecord(null);
      setIsSidebarClosing(false);
    }, 300);
  };

  const showToast = (message, isError = false) => {
    setToastMessage({ text: message, isError });
    setTimeout(() => setToastMessage(null), 3000);
  };

  const handleCopy = async (text, type) => {
    if (!text || text === '---') return;

    try {
      // Cách 1: Thử dùng API hiện đại (Chỉ chạy trên HTTPS hoặc localhost)
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
        showToast(`Đã copy ${type}: ${text}`);
      } else {
        // Cách 2: Fallback (Dự phòng) cho mạng LAN HTTP (192.168.x.x) hoặc Electron
        const textArea = document.createElement("textarea");
        textArea.value = text;
        
        // Đẩy textarea ra khỏi màn hình hiển thị để tránh giật giao diện
        textArea.style.position = "absolute";
        textArea.style.left = "-999999px";
        
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();

        try {
          // Sử dụng lệnh copy cổ điển
          document.execCommand('copy');
          showToast(`Đã copy ${type}: ${text}`);
        } catch (err) {
          console.error('Lỗi khi copy fallback:', err);
          showToast('Trình duyệt của bạn đang chặn chức năng Copy!', true);
        }
        
        // Xóa textarea sau khi copy xong
        document.body.removeChild(textArea);
      }
    } catch (err) {
      console.error('Failed to copy: ', err);
      showToast('Có lỗi xảy ra khi copy!', true);
    }
  };

  const renderXmlContent = (data) => {
    if (!data) return null;
    const dataArray = Array.isArray(data) ? data : [data];

    if (dataArray.length === 0) {
      return <p className="text-slate-400 text-sm italic p-4">Danh sách trống</p>;
    }

    const columns = Object.keys(dataArray[0]);

    return (
      <div className="overflow-auto border border-slate-200/60 rounded-xl bg-white shadow-sm custom-scrollbar max-h-[75vh]">
        <table className="w-full text-left whitespace-nowrap text-xs">
          <thead className="bg-slate-50 sticky top-0 z-10 shadow-sm">
            <tr>
              <th className="px-3 py-2.5 text-slate-500 font-bold uppercase tracking-wider w-10 text-center bg-slate-50 border-b border-slate-200">STT</th>
              {columns.map(col => (
                <th key={col} className="px-4 py-2.5 text-slate-500 font-bold uppercase tracking-wider bg-slate-50 border-b border-slate-200">
                  {col}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {dataArray.map((row, idx) => (
              <tr key={idx} className="hover:bg-sky-50/50 transition-colors">
                <td className="px-3 py-2 text-center font-mono text-slate-400 bg-white">{idx + 1}</td>
                {columns.map(col => {
                  const val = row[col];
                  const displayVal = (val !== null && val !== "") ? String(val) : "-";
                  return (
                    <td key={col} className={`px-4 py-2 font-mono ${displayVal === "-" ? "text-slate-300 text-center" : "text-slate-700 bg-white"}`}>
                      {displayVal}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div className="flex flex-col h-full w-full min-h-0 text-slate-800 font-sans relative gap-3 md:gap-4 p-2 md:p-0">
      
      {/* TOAST */}
      {toastMessage && createPortal(
        <div className={`fixed bottom-6 md:bottom-8 right-4 md:right-8 z-[9999] text-white px-5 py-3 rounded-xl shadow-xl flex items-center gap-3 animate-slide-up-fade ${toastMessage.isError ? 'bg-rose-600' : 'bg-slate-800'}`}>
          {toastMessage.isError ? <AlertCircle size={16} className="text-rose-200" /> : <CheckCircle2 size={16} className="text-emerald-400" />}
          <span className="text-sm font-semibold">{toastMessage.text}</span>
        </div>, document.body
      )}

      {/* DELETE MODAL */}
      {showDeleteModal && createPortal(
        <div className="fixed inset-0 z-[99999] flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm animate-fade-in">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6 flex flex-col gap-4 animate-slide-up-fade border border-slate-100">
            <div className="flex items-center gap-3 text-rose-600">
              <div className="bg-rose-100 p-2.5 rounded-full shrink-0"><Trash2 size={24} /></div>
              <h3 className="text-lg font-bold text-slate-800 tracking-tight">Xác nhận xóa dữ liệu</h3>
            </div>
            <div className="text-sm text-slate-600 leading-relaxed">
              Hệ thống sẽ xóa vĩnh viễn toàn bộ hồ sơ XML, 15 bảng liên quan và lỗi phát sinh từ ngày <strong className="text-slate-800 bg-slate-100 px-1.5 py-0.5 rounded">{formatDateVN(fromDate)}</strong> đến ngày <strong className="text-slate-800 bg-slate-100 px-1.5 py-0.5 rounded">{formatDateVN(toDate)}</strong>.
            </div>
            <div className="bg-rose-50 border border-rose-100 rounded-xl p-3 flex items-start gap-2">
              <AlertCircle size={16} className="text-rose-500 shrink-0 mt-0.5" />
              <p className="text-[13px] text-rose-700 font-medium leading-snug">Hành động này không thể hoàn tác.</p>
            </div>
            <div className="flex justify-end gap-3 mt-2">
              <button onClick={() => setShowDeleteModal(false)} disabled={isDeleting} className="px-4 py-2 rounded-xl text-sm font-semibold text-slate-600 hover:bg-slate-100 transition-colors">Hủy bỏ</button>
              <button onClick={handleDeleteData} disabled={isDeleting} className="px-5 py-2 rounded-xl text-sm font-bold bg-rose-600 text-white hover:bg-rose-700 transition-all flex items-center gap-2">
                {isDeleting ? <Loader2 size={16} className="animate-spin" /> : <Trash2 size={16} />} Đồng ý xóa
              </button>
            </div>
          </div>
        </div>, document.body
      )}

      {/* XML DETAILS MODAL */}
      {xmlDetailsModal.visible && createPortal(
        <div className="fixed inset-0 z-[100000] flex flex-col bg-slate-50 animate-fade-in">
          {/* Header */}
          <div className="bg-white/80 backdrop-blur-xl border-b border-slate-200 px-4 py-3 md:px-6 md:py-4 flex justify-between items-center shadow-sm shrink-0">
            <div className="flex items-center gap-4">
              <div className="bg-sky-100 p-2 md:p-2.5 rounded-xl text-sky-600">
                <FileJson size={24} />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h2 className="text-lg md:text-xl font-bold text-slate-800 tracking-tight font-mono">
                    {xmlDetailsModal.data?.ma_lk || 'Đang tải...'}
                  </h2>
                  {xmlDetailsModal.data?.errors?.length > 0 && (
                    <span className="bg-rose-100 text-rose-600 text-[10px] md:text-xs font-bold px-2 py-0.5 rounded-md border border-rose-200">
                      {xmlDetailsModal.data.errors.length} Lỗi
                    </span>
                  )}
                </div>
                <p className="text-xs md:text-sm text-slate-500 font-medium mt-0.5">Chi tiết dữ liệu 15 bảng XML</p>
              </div>
            </div>
            <button onClick={() => setXmlDetailsModal({ visible: false, data: null, loading: false, activeTab: '' })} className="p-2 bg-slate-100 text-slate-500 rounded-xl hover:bg-slate-200 hover:text-slate-800 transition-colors">
              <X size={24} />
            </button>
          </div>

          {/* Body */}
          <div className="flex flex-1 overflow-hidden">
            {xmlDetailsModal.loading ? (
              <div className="w-full flex flex-col items-center justify-center text-slate-400">
                <Loader2 size={40} className="animate-spin mb-4 text-sky-500" />
                <p className="font-semibold">Đang tải dữ liệu hồ sơ...</p>
              </div>
            ) : xmlDetailsModal.data ? (
              <>
                <div className="w-24 md:w-40 bg-white/60 backdrop-blur-md border-r border-slate-200 overflow-y-auto custom-scrollbar p-2 flex flex-col gap-1 shrink-0">
                  <button 
                    onClick={() => setXmlDetailsModal(s => ({ ...s, activeTab: 'LỖI' }))}
                    className={`w-full text-left px-3 py-2 rounded-lg text-xs md:text-sm font-bold transition-all flex items-center justify-between ${xmlDetailsModal.activeTab === 'LỖI' ? 'bg-rose-100 text-rose-700 shadow-sm' : 'text-slate-600 hover:bg-slate-100'}`}
                  >
                    <span>Lỗi</span>
                    {xmlDetailsModal.data.errors.length > 0 && <span className="bg-rose-500 text-white text-[10px] px-1.5 py-0.5 rounded">{xmlDetailsModal.data.errors.length}</span>}
                  </button>
                  <div className="h-px bg-slate-200 my-1"></div>
                  {Object.keys(xmlDetailsModal.data)
                    .filter(k => k.startsWith('XML') && xmlDetailsModal.data[k])
                    .sort((a, b) => parseInt(a.replace('XML', '')) - parseInt(b.replace('XML', '')))
                    .map(tabKey => (
                      <button 
                        key={tabKey}
                        onClick={() => setXmlDetailsModal(s => ({ ...s, activeTab: tabKey }))}
                        className={`w-full text-left px-3 py-2.5 rounded-lg text-xs md:text-sm font-bold font-mono transition-all ${xmlDetailsModal.activeTab === tabKey ? 'bg-sky-100 text-sky-700 shadow-sm' : 'text-slate-600 hover:bg-slate-100'}`}
                      >
                        Bảng {tabKey}
                      </button>
                    ))}
                </div>

                <div className="flex-1 bg-slate-50/50 p-3 md:p-6 overflow-y-auto custom-scrollbar relative">
                  {xmlDetailsModal.activeTab === 'LỖI' ? (
                    <div className="flex flex-col gap-3">
                      <h3 className="text-sm font-bold text-slate-700 uppercase tracking-widest flex items-center gap-2 mb-2"><AlertCircle size={16} className="text-rose-500"/> Danh sách lỗi phát hiện</h3>
                      {xmlDetailsModal.data.errors.length === 0 ? (
                        <div className="bg-emerald-50 text-emerald-600 p-4 rounded-xl border border-emerald-100 font-semibold flex items-center gap-2"><CheckCircle2 size={18}/> Hồ sơ hợp lệ, không có lỗi.</div>
                      ) : (
                        xmlDetailsModal.data.errors.map((err, i) => (
                          <div key={i} className="bg-white border-l-4 border-rose-400 rounded-xl p-4 shadow-sm">
                            <div className="flex items-center gap-2 mb-1.5">
                              <span className="text-[10px] font-mono font-bold bg-slate-100 text-slate-600 px-2 py-0.5 rounded border border-slate-200">Tag: {err.tag_path || '---'}</span>
                              <span className="text-[10px] font-bold bg-rose-50 text-rose-600 px-2 py-0.5 rounded border border-rose-100">{err.error_code}</span>
                            </div>
                            <p className="text-sm font-medium text-slate-700">{err.description}</p>
                          </div>
                        ))
                      )}
                    </div>
                  ) : (
                    <div className="flex flex-col h-full animate-fade-in">
                      <h3 className="text-sm font-bold text-slate-700 uppercase tracking-widest flex items-center gap-2 mb-4"><LayoutTemplate size={16} className="text-sky-500"/> Dữ liệu {xmlDetailsModal.activeTab}</h3>
                      {renderXmlContent(xmlDetailsModal.data[xmlDetailsModal.activeTab])}
                    </div>
                  )}
                </div>
              </>
            ) : null}
          </div>
        </div>, document.body
      )}

      {/* ================= TOOLBAR ================= */}
      <div className="shrink-0 bg-white/70 backdrop-blur-xl border border-white rounded-2xl p-3 md:px-5 md:py-3 flex flex-col xl:flex-row justify-between items-start xl:items-center shadow-sm gap-3 z-10">
        
        {/* Title Group & Toggle View */}
        <div className="flex w-full xl:w-auto items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="p-1.5 md:p-2 bg-sky-50 text-sky-600 rounded-lg">
              <Layers size={18} strokeWidth={2.5} />
            </div>
            <div className="flex items-center bg-slate-100/80 p-1 rounded-xl">
              <button 
                onClick={() => { setViewMode('ERRORS'); setSelectedErrorCode('ALL'); setSearchTerm(''); }}
                className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${viewMode === 'ERRORS' ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
              >
                Hồ sơ lỗi
              </button>
              <button 
                onClick={() => { setViewMode('ALL'); setSelectedErrorCode('ALL'); setSearchTerm(''); }}
                className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all flex items-center gap-1.5 ${viewMode === 'ALL' ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
              >
                <List size={14} /> Tất cả hồ sơ
              </button>
            </div>
          </div>
        </div>

        {/* Action & Filter Group */}
        <div className="flex flex-col xl:flex-row items-stretch xl:items-center gap-2 md:gap-3 w-full xl:w-auto">
          
          {/* MỚI: Ô Tìm kiếm */}
          <div className="flex items-center bg-white/80 p-1.5 rounded-xl border border-slate-200/60 shadow-sm w-full xl:w-48 overflow-hidden">
            <Search size={14} className="text-slate-400 ml-1 mr-2" />
            <input 
              type="text" 
              placeholder="Tìm LK, BN, Tên..." 
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="bg-transparent text-xs font-semibold text-slate-700 outline-none w-full placeholder:text-slate-400 placeholder:font-normal" 
            />
            {searchTerm && (
              <button onClick={() => setSearchTerm('')} className="text-slate-400 hover:text-rose-500 p-0.5">
                <X size={14} />
              </button>
            )}
          </div>

          <div className="flex items-center justify-between gap-2 bg-white/80 p-1 md:p-1.5 rounded-xl border border-slate-200/60 shadow-sm w-full xl:w-auto overflow-hidden">
            <div className="flex items-center gap-1 md:gap-2 px-2 flex-1 xl:flex-none justify-center">
              <Calendar size={14} className="text-slate-400 hidden sm:block" />
              <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} className="bg-transparent text-[11px] md:text-xs font-semibold text-slate-700 outline-none cursor-pointer w-full max-w-[105px]" />
              <span className="text-slate-300">-</span>
              <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} className="bg-transparent text-[11px] md:text-xs font-semibold text-slate-700 outline-none cursor-pointer w-full max-w-[105px]" />
            </div>
            <button onClick={fetchData} disabled={loading || isRechecking || isRecheckingErrors || isDeleting} className="bg-sky-600 text-white px-3 py-1.5 rounded-lg text-[11px] md:text-xs font-semibold hover:bg-sky-700 transition-all shadow-sm flex items-center justify-center gap-1.5 active:scale-95 disabled:opacity-70 shrink-0">
              {loading ? <Loader2 size={14} className="animate-spin" /> : <Search size={14} />} <span className="hidden sm:inline">Tìm</span>
            </button>
          </div>

          <div className="flex flex-wrap items-center gap-2 w-full xl:w-auto">
            <button 
              onClick={handleRecheckErrorsOnly}
              disabled={isRechecking || isRecheckingErrors || loading || isDeleting || allSubmissionsRaw.filter(s=>s.status!=='Hop_le').length === 0}
              className={`flex-1 xl:flex-none px-2 md:px-3 py-2 md:py-1.5 rounded-xl text-[11px] md:text-xs font-semibold transition-all shadow-sm flex justify-center items-center gap-1.5 border ${
                isRecheckingErrors ? 'bg-amber-50 text-amber-600 border-amber-100 cursor-wait' : 'bg-white text-amber-600 border-amber-200 hover:bg-amber-50 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed'
              }`}
            >
              {isRecheckingErrors ? <Loader2 size={14} className="animate-spin" /> : <AlertCircle size={14} />}
              <span className="whitespace-nowrap">Chạy lại HS lỗi</span>
            </button>

            <button 
              onClick={handleRecheck}
              disabled={isRechecking || isRecheckingErrors || loading || isDeleting}
              className={`flex-1 xl:flex-none px-2 md:px-3 py-2 md:py-1.5 rounded-xl text-[11px] md:text-xs font-semibold transition-all shadow-sm flex justify-center items-center gap-1.5 border ${
                isRechecking ? 'bg-emerald-50 text-emerald-500 border-emerald-100 cursor-wait' : 'bg-white text-emerald-600 border-emerald-200 hover:bg-emerald-50 active:scale-95'
              }`}
            >
              {isRechecking ? <Loader2 size={14} className="animate-spin" /> : <Zap size={14} />}
              <span className="whitespace-nowrap">Chạy lại tất cả</span>
            </button>

            <button 
              onClick={() => setShowDeleteModal(true)}
              disabled={isRechecking || isRecheckingErrors || loading || isDeleting}
              className={`flex-1 xl:flex-none px-2 md:px-3 py-2 md:py-1.5 rounded-xl text-[11px] md:text-xs font-semibold transition-all shadow-sm flex justify-center items-center gap-1.5 border bg-white text-rose-600 border-rose-200 hover:bg-rose-50 active:scale-95 disabled:opacity-60 disabled:cursor-not-allowed`}
            >
              <Trash2 size={14} />
              <span className="whitespace-nowrap">Xóa dữ liệu</span>
            </button>
          </div>
        </div>
      </div>

      {/* ================= NỘI DUNG CHÍNH ================= */}
      <div className="flex flex-col md:flex-row flex-1 overflow-hidden min-h-0 gap-3 md:gap-4 relative">
        
        {/* Lớp phủ Loading khi đang làm mới hoặc gọi API */}
        {(loading || isRechecking || isRecheckingErrors || isDeleting) && (
          <div className="absolute inset-0 bg-white/40 backdrop-blur-[2px] z-20 flex flex-col items-center justify-center rounded-2xl">
            <Loader2 size={40} className="text-sky-500 animate-spin mb-3" />
            <p className="text-sm font-bold text-slate-700 bg-white/80 px-4 py-2 rounded-xl shadow-sm border border-slate-200">
              Hệ thống đang xử lý, vui lòng chờ...
            </p>
          </div>
        )}

        {/* Desktop View Sidebar Phân loại lỗi */}
        <div className="hidden md:flex w-64 bg-white/70 backdrop-blur-xl rounded-2xl border border-white shadow-sm flex-col overflow-hidden shrink-0">
          <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between shrink-0 bg-white/40">
            <h3 className="text-xs font-bold text-slate-600 uppercase tracking-tight flex items-center gap-2"><Filter size={14} className="text-sky-500" /> Phân loại {viewMode === 'ALL' ? 'hồ sơ' : 'lỗi'}</h3>
          </div>

          <div className="flex-1 overflow-y-auto custom-scrollbar p-2 space-y-1">
            <button 
              onClick={() => setSelectedErrorCode('ALL')}
              className={`w-full text-left p-2.5 rounded-xl flex items-center justify-between transition-all ${selectedErrorCode === 'ALL' ? 'bg-sky-50 border-sky-100 border text-sky-700 shadow-sm' : 'hover:bg-white/60 border border-transparent text-slate-600'}`}
            >
              <span className="text-xs font-semibold">Tất cả ({viewMode === 'ALL' ? 'Tổng' : 'Lỗi'})</span>
              <span className={`text-[10px] font-bold px-2 py-0.5 rounded-md ${selectedErrorCode === 'ALL' ? 'bg-sky-200 text-sky-800' : 'bg-slate-100 text-slate-500'}`}>{activeSubmissions.length}</span>
            </button>
            <div className="my-1.5 border-t border-dashed border-slate-200"></div>

            {groupedErrors.map((group, idx) => {
              const isActive = selectedErrorCode === group.code;
              return (
                <button 
                  key={idx} onClick={() => setSelectedErrorCode(group.code)}
                  className={`w-full text-left p-2.5 rounded-xl flex items-start justify-between transition-all gap-2 ${isActive ? 'bg-amber-50 border-amber-100 border text-amber-700 shadow-sm' : 'hover:bg-white/60 border border-transparent text-slate-600'}`}
                >
                  <div className="flex-1">
                    <p className={`text-[11px] font-mono font-bold mb-0.5 ${isActive ? 'text-amber-600' : 'text-slate-700'}`}>{group.code}</p>
                    <p className={`text-[10px] leading-tight ${isActive ? 'text-amber-600 font-semibold' : 'text-slate-500'}`}>{group.name}</p>
                  </div>
                  <span className={`shrink-0 text-[10px] font-bold px-1.5 py-0.5 rounded-md ${isActive ? 'bg-amber-200 text-amber-800' : 'bg-slate-100 text-slate-500'}`}>{group.records.size}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Mobile View Categories (Nút cuộn ngang) */}
        <div className="md:hidden flex overflow-x-auto custom-scrollbar gap-2 pb-2 shrink-0 px-1 snap-x">
          <button onClick={() => setSelectedErrorCode('ALL')} className={`shrink-0 snap-start px-3 py-2 rounded-xl flex items-center gap-2 border transition-all text-xs font-semibold ${selectedErrorCode === 'ALL' ? 'bg-sky-50 border-sky-200 text-sky-700 shadow-sm' : 'bg-white border-slate-200 text-slate-600'}`}>
            <span>Tất cả</span>
            <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-md ${selectedErrorCode === 'ALL' ? 'bg-sky-200 text-sky-800' : 'bg-slate-100 text-slate-500'}`}>{activeSubmissions.length}</span>
          </button>
          {groupedErrors.map((group, idx) => {
            const isActive = selectedErrorCode === group.code;
            return (
              <button key={idx} onClick={() => setSelectedErrorCode(group.code)} className={`shrink-0 snap-start px-3 py-2 rounded-xl flex items-center gap-2 border transition-all ${isActive ? 'bg-amber-50 border-amber-200 text-amber-700 shadow-sm' : 'bg-white border-slate-200 text-slate-600'}`}>
                <div className="flex flex-col text-left">
                  <span className={`text-[11px] font-mono font-bold leading-none mb-0.5 ${isActive ? 'text-amber-600' : 'text-slate-700'}`}>{group.code}</span>
                  <span className="text-[9px] truncate max-w-[120px] text-slate-500 font-medium">{group.name}</span>
                </div>
                <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-md shrink-0 ${isActive ? 'bg-amber-200 text-amber-800' : 'bg-slate-100 text-slate-500'}`}>{group.records.size}</span>
              </button>
            );
          })}
        </div>

        {/* Cột Phải (Table/Cards) */}
        <div className="flex-1 bg-transparent md:bg-white/70 backdrop-blur-xl md:rounded-2xl md:border border-white md:shadow-sm flex flex-col overflow-hidden relative">
          <div className="hidden md:flex px-5 py-3 border-b border-slate-100 justify-between items-center bg-white/40 shrink-0">
            <div>
              <h2 className="text-sm font-bold text-slate-800">Danh sách hiển thị ({filteredAndSearchedRecords.length})</h2>
              <p className="text-[10px] text-slate-500 mt-0.5">Click đúp vào dòng để xem cảnh báo, click icon Mắt để xem toàn bộ XML</p>
            </div>
            {/* Phân trang header (chọn size) */}
            <div className="flex items-center gap-2">
              <span className="text-xs text-slate-500 font-medium">Hiển thị:</span>
              <select 
                value={pageSize}
                onChange={(e) => setPageSize(Number(e.target.value))}
                className="bg-white border border-slate-200 text-xs text-slate-700 rounded-lg px-2 py-1 outline-none focus:border-sky-300"
              >
                <option value={20}>20</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </div>
          </div>

          <div className="md:hidden flex justify-between items-center mb-2 px-1 shrink-0">
            <h2 className="text-xs font-bold text-slate-700 uppercase tracking-tight">Danh sách ({filteredAndSearchedRecords.length})</h2>
          </div>

          <div className="flex-1 overflow-y-auto custom-scrollbar md:bg-white/30 relative pb-4 md:pb-0">
            {paginatedRecords.length === 0 && !loading ? (
               <div className="absolute inset-0 flex flex-col items-center justify-center text-slate-400 bg-white/50 rounded-2xl md:rounded-none">
                  <FileCheck2 size={40} className="text-slate-300 mb-3" />
                  <p className="font-semibold text-slate-500 text-sm">Không có dữ liệu phù hợp</p>
               </div>
            ) : (
              <>
                {/* ---------------- BẢNG HIỂN THỊ (DESKTOP) ---------------- */}
                <div className="hidden md:block w-full">
                  <table className="w-full text-left border-collapse min-w-[850px]">
                    <thead className="bg-slate-50/90 backdrop-blur-md sticky top-0 z-10 shadow-sm">
                      <tr className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">
                        <th className="px-4 py-3 border-b border-slate-200">Mã Liên Kết</th>
                        <th className="px-4 py-3 border-b border-slate-200">Mã BN</th>
                        <th className="px-4 py-3 border-b border-slate-200">Họ và Tên</th>
                        <th className="px-3 py-3 border-b border-slate-200 text-center">Loại KCB</th>
                        <th className="px-3 py-3 border-b border-slate-200 text-center">Ngày vào / ra</th>
                        <th className="px-4 py-3 border-b border-slate-200 text-center">Lỗi</th>
                        <th className="px-4 py-3 border-b border-slate-200 text-center">XML</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 bg-white/40">
                      {paginatedRecords.map((sub) => (
                        <tr key={sub.id} onDoubleClick={() => { if(sub.errors?.length > 0) setInspectingRecord(sub); else openXmlDetails(sub.id); }} className="hover:bg-sky-50/50 transition-colors group cursor-pointer">
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2 w-fit cursor-pointer relative group/copy" onClick={(e) => { e.stopPropagation(); handleCopy(sub.ma_lk, 'Mã LK'); }}>
                              <Fingerprint size={14} className="text-slate-400 group-hover:text-sky-500" />
                              <span className="font-mono font-semibold text-xs text-slate-700 group-hover:text-sky-700">{sub.ma_lk}</span>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <div className="font-mono font-medium text-xs text-slate-600 bg-white px-1.5 py-0.5 rounded cursor-pointer transition-colors w-fit border border-slate-100 hover:border-sky-200 hover:text-sky-600" onClick={(e) => { e.stopPropagation(); handleCopy(sub.ma_bn, 'Mã BN'); }}>
                              {sub.ma_bn || '---'}
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              <UserCircle2 size={14} className="text-slate-400" />
                              <span className="font-semibold text-xs text-slate-800">{sub.ho_ten || 'Đang cập nhật'}</span>
                            </div>
                          </td>
                          <td className="px-3 py-3 text-center">
                            <span className="text-[10px] font-semibold text-indigo-600 bg-indigo-50 px-2 py-0.5 rounded border border-indigo-100 whitespace-nowrap">{getLoaiKCBName(sub.ma_loai_kcb)}</span>
                          </td>
                          <td className="px-3 py-3 text-center">
                            <div className="flex flex-col gap-0.5">
                              <span className="text-[10px] text-slate-500 whitespace-nowrap">V: {formatDateTime(sub.ngay_vao)}</span>
                              <span className="text-[10px] text-slate-600 font-medium whitespace-nowrap">R: {formatDateTime(sub.ngay_ra)}</span>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-center">
                            {sub.errors?.length > 0 ? (
                              <span className="inline-flex items-center justify-center min-w-[24px] h-6 px-1.5 rounded-lg bg-amber-50 text-amber-600 font-bold text-[11px] border border-amber-100 group-hover:bg-amber-500 group-hover:text-white transition-all">
                                {sub.errors.length}
                              </span>
                            ) : (
                              <span className="inline-flex items-center justify-center h-6 px-2 rounded-lg bg-emerald-50 text-emerald-600 font-bold text-[10px] border border-emerald-100"><CheckCircle2 size={12} className="mr-1"/> Hợp lệ</span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-center">
                            <button onClick={(e) => { e.stopPropagation(); openXmlDetails(sub.id); }} className="p-1.5 text-sky-500 hover:bg-sky-100 hover:text-sky-700 rounded-lg transition-colors inline-flex" title="Xem chi tiết các bảng XML">
                              <Eye size={16} />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* ---------------- DANH SÁCH THẺ (MOBILE) ---------------- */}
                <div className="md:hidden flex flex-col gap-3">
                  {paginatedRecords.map((sub) => (
                    <div key={sub.id} onClick={() => openXmlDetails(sub.id)} className="bg-white rounded-2xl shadow-sm border border-slate-200 p-4 flex flex-col gap-3 relative active:scale-[0.98] transition-transform">
                      <div className="flex justify-between items-start">
                        <div className="flex items-center gap-1.5 bg-slate-50 px-2 py-1 rounded-lg border border-slate-100 cursor-pointer active:bg-slate-100" onClick={(e) => { e.stopPropagation(); handleCopy(sub.ma_lk, 'Mã LK'); }}>
                          <Fingerprint size={14} className="text-sky-500" />
                          <span className="font-mono font-bold text-xs text-slate-700">{sub.ma_lk}</span>
                        </div>
                        {sub.errors?.length > 0 ? (
                          <div className="flex items-center gap-1 bg-amber-50 text-amber-600 px-2 py-1 rounded-lg border border-amber-100 font-bold text-[11px]" onClick={(e) => { e.stopPropagation(); setInspectingRecord(sub); }}>
                            <AlertCircle size={12} /> {sub.errors.length} Lỗi
                          </div>
                        ) : (
                          <div className="flex items-center gap-1 bg-emerald-50 text-emerald-600 px-2 py-1 rounded-lg border border-emerald-100 font-bold text-[11px]">
                            <CheckCircle2 size={12} /> Hợp lệ
                          </div>
                        )}
                      </div>

                      <div>
                        <div className="flex items-center gap-2">
                          <UserCircle2 size={16} className="text-slate-400" />
                          <span className="font-bold text-sm text-slate-800">{sub.ho_ten || 'Đang cập nhật'}</span>
                        </div>
                        <div className="ml-6 mt-1 text-xs font-mono font-medium text-slate-500 cursor-pointer active:text-sky-600 w-fit" onClick={(e) => { e.stopPropagation(); handleCopy(sub.ma_bn, 'Mã BN'); }}>
                          Mã BN: {sub.ma_bn || '---'}
                        </div>
                      </div>

                      <div className="flex justify-between items-end border-t border-slate-100 pt-3 mt-1">
                        <span className="text-[10px] font-semibold text-indigo-600 bg-indigo-50 px-2 py-1 rounded-md border border-indigo-100">{getLoaiKCBName(sub.ma_loai_kcb)}</span>
                        <div className="flex flex-col items-end gap-1 text-[10px] text-slate-500 font-medium">
                          <div className="flex items-center gap-1"><span className="w-8 text-right text-slate-400">Vào:</span><span className="text-slate-700">{formatDateTime(sub.ngay_vao)}</span></div>
                          <div className="flex items-center gap-1"><span className="w-8 text-right text-slate-400">Ra:</span><span className="text-slate-700">{formatDateTime(sub.ngay_ra)}</span></div>
                        </div>
                      </div>
                      <div className="absolute right-3 top-1/2 -translate-y-1/2 text-sky-400 opacity-60">
                         <Eye size={22} strokeWidth={1.5} />
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>

          {/* Phân Trang (Pagination Controls) */}
          {totalPages > 1 && (
            <div className="px-4 py-3 bg-white/80 border-t border-slate-100 flex items-center justify-between shrink-0">
              <span className="text-xs text-slate-500 font-medium">
                Trang {currentPage} / {totalPages}
              </span>
              <div className="flex items-center gap-2">
                <button 
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="p-1.5 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronLeft size={16} />
                </button>
                <button 
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className="p-1.5 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronRight size={16} />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* SIDEBAR CHI TIẾT LỖI CŨ */}
      {inspectingRecord && createPortal(
        <div className="fixed inset-0 z-[9999] flex justify-end overflow-hidden">
          <div className={`absolute inset-0 bg-slate-900/40 backdrop-blur-sm ${isSidebarClosing ? 'animate-fade-out' : 'animate-fade-in'}`} onClick={closeSidebar}></div>
          <div className={`relative w-full max-w-full sm:max-w-md bg-white h-full shadow-2xl flex flex-col border-l border-slate-200 ${isSidebarClosing ? 'animate-slide-out' : 'animate-slide-in'}`}>
            <div className="px-4 py-4 md:px-6 md:py-5 bg-white flex justify-between items-start border-b border-slate-100 shrink-0 z-10 shadow-sm">
              <div className="flex gap-3 md:gap-4 items-center">
                <div className="w-10 h-10 md:w-12 md:h-12 bg-amber-50 text-amber-500 rounded-xl md:rounded-2xl flex items-center justify-center shrink-0">
                  <AlertCircle size={22} strokeWidth={2.5} className="md:w-6 md:h-6" />
                </div>
                <div className="flex flex-col gap-1">
                  <div className="flex items-center gap-2 cursor-pointer active:text-sky-600 group/copy w-fit" onClick={() => handleCopy(inspectingRecord.ma_lk, 'Mã LK')}>
                    <h3 className="text-base md:text-lg font-mono font-bold leading-none text-slate-800 md:group-hover/copy:text-sky-600 transition-colors">{inspectingRecord.ma_lk}</h3>
                  </div>
                  <div className="flex flex-wrap items-center gap-1.5 md:gap-2">
                    <span className="text-[10px] md:text-[11px] font-bold bg-slate-100 text-slate-600 px-1.5 py-0.5 md:py-1 rounded uppercase max-w-[150px] truncate">{inspectingRecord.ho_ten || 'Không tên'}</span>
                    <span className="text-[10px] md:text-[11px] font-mono font-semibold text-slate-500 bg-slate-50 px-1.5 py-0.5 md:py-1 rounded cursor-pointer border border-transparent active:border-sky-200 active:bg-sky-50 active:text-sky-600 transition-colors" onClick={() => handleCopy(inspectingRecord.ma_bn, 'Mã BN')}>
                      Mã BN: {inspectingRecord.ma_bn || '---'}
                    </span>
                  </div>
                </div>
              </div>
              <button onClick={closeSidebar} className="p-2 md:p-1.5 bg-slate-100 hover:bg-rose-50 hover:text-rose-600 active:scale-95 rounded-xl md:rounded-lg text-slate-500 transition-all shrink-0"><X size={20} strokeWidth={2.5} /></button>
            </div>
            <div className="flex-1 overflow-y-auto p-4 md:p-6 space-y-3 custom-scrollbar bg-[#f8fafc]">
              <div className="flex items-center justify-between mb-2">
                 <h4 className="text-[11px] md:text-xs font-bold text-slate-500 uppercase tracking-widest">Chi tiết {inspectingRecord.errors?.length} sai sót</h4>
              </div>
              {inspectingRecord.errors.map((err, idx) => (
                <div key={idx} className="bg-white border border-slate-200 rounded-xl p-3 md:p-4 shadow-sm relative overflow-hidden flex flex-col gap-2">
                  <div className="absolute left-0 top-0 bottom-0 w-1 bg-amber-400"></div>
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 ml-1.5 md:ml-2">
                    <span className="text-[10px] md:text-[11px] font-mono font-semibold bg-slate-50 text-slate-600 px-2 py-1 rounded border border-slate-100 break-all">Tag: {err.tag_path || 'XML_TAG'}</span>
                    <span className="text-[9px] md:text-[10px] font-bold text-amber-600 bg-amber-50 px-2 py-1 rounded border border-amber-100 uppercase tracking-widest shrink-0 sm:ml-2 w-fit">{err.error_code}</span>
                  </div>
                  <p className="text-xs md:text-sm font-medium text-slate-700 leading-relaxed ml-1.5 md:ml-2">{err.description}</p>
                </div>
              ))}
              <div className="h-4 md:h-0"></div>
            </div>
            <div className="p-4 bg-white border-t border-slate-100 shrink-0 pb-safe">
              <button onClick={closeSidebar} className="w-full py-3 md:py-2.5 bg-sky-600 text-white rounded-xl md:rounded-lg font-bold text-sm md:text-base hover:bg-sky-700 active:scale-[0.98] transition-all shadow-sm">Đóng chi tiết</button>
            </div>
          </div>
        </div>, document.body
      )}

      {/* KEYFRAMES CSS */}
      <style>{`
        .custom-scrollbar::-webkit-scrollbar { width: 4px; height: 4px; }
        .custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
        .custom-scrollbar::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 8px; }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover { background: #94a3b8; }
        .pb-safe { padding-bottom: max(1rem, env(safe-area-inset-bottom)); }
        @keyframes slideInRight { from { transform: translateX(100%); } to { transform: translateX(0); } }
        .animate-slide-in { animation: slideInRight 0.3s cubic-bezier(0.16, 1, 0.3, 1) forwards; }
        @keyframes slideOutRight { from { transform: translateX(0); } to { transform: translateX(100%); } }
        .animate-slide-out { animation: slideOutRight 0.3s cubic-bezier(0.16, 1, 0.3, 1) forwards; }
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        .animate-fade-in { animation: fadeIn 0.25s ease-out forwards; }
        @keyframes fadeOut { from { opacity: 1; } to { opacity: 0; } }
        .animate-fade-out { animation: fadeOut 0.25s ease-out forwards; }
        @keyframes slideUpFade { from { opacity: 0; transform: translateY(15px); } to { opacity: 1; transform: translateY(0); } }
        .animate-slide-up-fade { animation: slideUpFade 0.25s cubic-bezier(0.16, 1, 0.3, 1) forwards; }
      `}</style>
    </div>
  );
};

export default XmlErrorDataGrid;